import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  BookOpen, MessageCircle, PenTool, FileText, Users, 
  Library, GraduationCap, HelpCircle, ChevronRight, ChevronLeft,
  X, Sparkles, Check, ArrowRight
} from "lucide-react";

const TUTORIAL_STEPS = [
  {
    id: "welcome",
    title: "Bienvenue sur SophIA !",
    description: "Je suis votre professeur de philosophie virtuel, disponible 24h/24 pour vous aider à préparer le Baccalauréat.",
    icon: Sparkles,
    color: "from-[#D4AF37] to-[#B3932E]",
    tips: [
      "SophIA signifie 'Sagesse' en grec ancien",
      "21 philosophes sont à votre disposition",
      "Tous les outils du programme de Terminale"
    ]
  },
  {
    id: "chat",
    title: "Posez vos questions",
    description: "Discutez en privé avec SophIA ou n'importe quel philosophe. Les philosophes peuvent même débattre entre eux !",
    icon: MessageCircle,
    color: "from-[#3B82F6] to-[#2563EB]",
    tips: [
      "Cliquez sur 'Chat Privé' dans le menu",
      "Les philosophes se répondent entre eux",
      "Style WhatsApp avec bulles et indicateurs"
    ],
    module: "chat-module"
  },
  {
    id: "dissertation",
    title: "Entraînez-vous à la Dissertation",
    description: "Rédigez des dissertations guidées avec des encadrés pour chaque partie: Introduction, Thèse, Antithèse, Synthèse, Conclusion.",
    icon: PenTool,
    color: "from-[#22C55E] to-[#16A34A]",
    tips: [
      "SophIA génère des pistes de réflexion",
      "Chaque section a des conseils méthodologiques",
      "Recevez une évaluation détaillée"
    ],
    module: "dissertation-module"
  },
  {
    id: "commentaire",
    title: "Commentaire de texte",
    description: "Analysez les grands textes philosophiques avec une méthode structurée. Le texte complet est affiché pour vous faciliter le travail.",
    icon: FileText,
    color: "from-[#8B5CF6] to-[#7C3AED]",
    tips: [
      "Textes de Platon, Descartes, Kant, Nietzsche...",
      "Encadrés séparés pour chaque partie",
      "Conseils pour l'introduction et la problématique"
    ],
    module: "commentary-module"
  },
  {
    id: "thematiques",
    title: "Les 17 Notions du Bac",
    description: "Explorez chaque thème du programme : La vérité, La liberté, L'État... Découvrez les positions de chaque philosophe !",
    icon: GraduationCap,
    color: "from-[#EC4899] to-[#DB2777]",
    tips: [
      "L'art, Le bonheur, La conscience, Le devoir...",
      "Citations avec accents français corrects",
      "Parfait pour construire thèse/antithèse"
    ],
    module: "thematiques-module"
  },
  {
    id: "library",
    title: "Bibliothèque des Philosophes",
    description: "Découvrez les 21 philosophes du programme avec leurs biographies, œuvres détaillées, et positions sur les thèmes du Bac.",
    icon: Library,
    color: "from-[#F59E0B] to-[#D97706]",
    tips: [
      "Biographies et concepts clés",
      "Analyse détaillée de chaque œuvre majeure",
      "Discutez directement avec chaque philosophe"
    ],
    module: "library-module"
  },
  {
    id: "agora",
    title: "L'Agora Philosophique",
    description: "Participez à des débats avec plusieurs philosophes en même temps ! Choisissez un sujet et voyez les différentes perspectives.",
    icon: Users,
    color: "from-[#06B6D4] to-[#0891B2]",
    tips: [
      "Débattez sur un thème avec 3 philosophes",
      "Comparez les points de vue",
      "Enrichissez votre réflexion"
    ],
    module: "debate-module"
  },
  {
    id: "quiz",
    title: "Quiz de Remédiation",
    description: "Testez vos connaissances avec des quiz sur les notions de base. Les mauvaises réponses sont réalistes pour vous aider à éviter les pièges !",
    icon: HelpCircle,
    color: "from-[#EF4444] to-[#DC2626]",
    tips: [
      "Questions sur les définitions et concepts",
      "Réponses basées sur les erreurs courantes",
      "Progressez à votre rythme"
    ],
    module: "quiz-module"
  },
  {
    id: "finish",
    title: "Vous êtes prêt !",
    description: "Vous connaissez maintenant tous les outils de SophIA. Bonne préparation pour le Bac !",
    icon: Check,
    color: "from-[#10B981] to-[#059669]",
    tips: [
      "N'hésitez pas à poser des questions",
      "Utilisez la bibliothèque pour réviser",
      "Entraînez-vous régulièrement"
    ]
  }
];

const InteractiveTutorial = ({ onComplete, onSkip }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  const step = TUTORIAL_STEPS[currentStep];
  const Icon = step.icon;
  const isLastStep = currentStep === TUTORIAL_STEPS.length - 1;
  const isFirstStep = currentStep === 0;

  const handleNext = () => {
    if (isLastStep) {
      handleComplete();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleComplete = () => {
    localStorage.setItem("sophia_tutorial_completed", "true");
    setIsVisible(false);
    if (onComplete) onComplete();
  };

  const handleSkip = () => {
    localStorage.setItem("sophia_tutorial_completed", "true");
    setIsVisible(false);
    if (onSkip) onSkip();
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg bg-white shadow-2xl border-0 overflow-hidden">
        {/* Header with gradient */}
        <div className={`bg-gradient-to-br ${step.color} p-6 text-white relative`}>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleSkip}
            className="absolute top-2 right-2 text-white/80 hover:text-white hover:bg-white/20"
          >
            <X className="w-5 h-5" />
          </Button>
          
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20 rounded-xl">
              <Icon className="w-8 h-8" />
            </div>
            <div>
              <Badge className="bg-white/20 text-white mb-1">
                Étape {currentStep + 1}/{TUTORIAL_STEPS.length}
              </Badge>
              <h2 className="text-xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>
                {step.title}
              </h2>
            </div>
          </div>
        </div>

        <CardContent className="p-6">
          <p className="text-[#0F172A] mb-6 leading-relaxed">
            {step.description}
          </p>

          {/* Tips */}
          <div className="space-y-2 mb-6">
            {step.tips.map((tip, i) => (
              <div key={i} className="flex items-start gap-2 text-sm text-[#64748B]">
                <ArrowRight className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                <span>{tip}</span>
              </div>
            ))}
          </div>

          {/* Progress dots */}
          <div className="flex justify-center gap-1.5 mb-6">
            {TUTORIAL_STEPS.map((_, i) => (
              <div
                key={i}
                className={`w-2 h-2 rounded-full transition-all ${
                  i === currentStep 
                    ? 'bg-[#1A365D] w-6' 
                    : i < currentStep 
                      ? 'bg-[#D4AF37]' 
                      : 'bg-[#E2E8F0]'
                }`}
              />
            ))}
          </div>

          {/* Navigation buttons */}
          <div className="flex justify-between gap-3">
            <Button
              variant="outline"
              onClick={handlePrev}
              disabled={isFirstStep}
              className="flex-1"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Précédent
            </Button>
            
            <Button
              onClick={handleNext}
              className={`flex-1 ${isLastStep ? 'bg-[#22C55E] hover:bg-[#16A34A]' : 'bg-[#1A365D] hover:bg-[#132745]'}`}
            >
              {isLastStep ? (
                <>
                  <Check className="w-4 h-4 mr-1" />
                  Commencer
                </>
              ) : (
                <>
                  Suivant
                  <ChevronRight className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          </div>

          {/* Skip link */}
          <button
            onClick={handleSkip}
            className="w-full mt-4 text-center text-sm text-[#64748B] hover:text-[#1A365D] transition-colors"
          >
            Passer le tutoriel
          </button>
        </CardContent>
      </Card>
    </div>
  );
};

export default InteractiveTutorial;
